// frontend/src/App.js
import React, { Suspense, lazy, useMemo } from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Link as RouterLink,
} from 'react-router-dom';
import { Box, Button, LinearProgress, Paper, Stack, Typography } from '@mui/material';

import Login from './components/Login';
import Layout from './components/Layout';

// SITE
import Dashboard from './components/SmartSiteDashboard';
const Reconciliation = lazy(() => import('./components/Reconciliation'));
const ExpectedTankers = lazy(() => import('./components/ExpectedTankers'));
const DailySales = lazy(() => import('./components/DailySales'));
const DriveOffNmopBoard = lazy(() => import('./components/DriveOffNmopBoard'));

// IMPORTANT:
// Use the updated Golden Lines page from /pages,
// not the older component version.
const GoldenLinesPage = lazy(() => import('./pages/GoldenLinesPage'));

// DIRECTORS
const WeeklyOverview = lazy(() => import('./components/WeeklyOverview'));
const DirectorsDailySales = lazy(() => import('./components/DirectorsDailySales'));
const DirectorsDashboard = lazy(() => import('./components/DirectorsDashboard'));
const DirectorsWeeklyReconciliation = lazy(() => import('./components/DirectorsWeeklyReconciliation'));
const DirectorsDailyReconciliation = lazy(() => import('./components/DirectorsDailyReconciliation'));
const GoldenLinesDirectorPage = lazy(() => import('./pages/GoldenLinesDirectorPage'));
// Password screens
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';

// Admin
const AdminUsers = lazy(() => import('./components/AdminUsers'));
const AdminSettings = lazy(() => import('./components/AdminSettings'));
const AdminHome = lazy(() => import('./components/admin/AdminHome'));
const AdminSites = lazy(() => import('./components/admin/AdminSites'));
const AdminDeliveries = lazy(() => import('./components/admin/AdminDeliveries'));

// BACS payments
const BeneficiaryDirectory = lazy(() => import('./components/payments/BeneficiaryDirectory'));
const DirectorsPaymentApproval = lazy(() => import('./components/payments/DirectorsPaymentApproval'));
const DirectorsPaymentsOverview = lazy(() => import('./components/payments/DirectorsPaymentsOverview'));
const PaymentsDuePage = lazy(() => import('./components/payments/PaymentsDuePage'));
const PaymentsWorkspace = lazy(() => import('./components/payments/PaymentsWorkspace'));
const QuarterlyPackPage = lazy(() => import('./components/payments/QuarterlyPackPage'));
const SpendAnalyticsPage = lazy(() => import('./components/payments/SpendAnalyticsPage'));
const SupplierStatementsPage = lazy(() => import('./components/payments/SupplierStatementsPage'));

import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PlatformProvider, usePlatform } from './contexts/PlatformContext';

// Golden Lines Manager
import GLManagerLogin from './components/GLManagerLogin';
const GLManagerShell = lazy(() => import('./components/GLManagerShell'));
const GLDashboardOverview = lazy(() => import('./components/GLDashboardOverview'));
const GLSalesPerformance = lazy(() => import('./components/GLSalesPerformance'));
const GLManageProducts = lazy(() => import('./components/GLManageProducts'));
const GLSites = lazy(() => import('./components/GLSites'));
const GLReports = lazy(() => import('./components/GLReports'));
import {
  GL_MANAGER_DEFAULT_WEEK_END,
  GL_MANAGER_DEFAULT_WEEK_START,
  managerPathWithWeek,
} from './utils/glManagerContext';

const DEFAULT_GL_MANAGER_OVERVIEW = managerPathWithWeek(
  '/gl-manager/overview',
  GL_MANAGER_DEFAULT_WEEK_START,
  GL_MANAGER_DEFAULT_WEEK_END
);


/*
 * The theme decides how this application looks. That sounds obvious; it was
 * not true. A theme existed, and then every panel painted its own gradient,
 * its own corner radius, its own shadow and its own pastel icon chip over
 * the top of it — so six cards in a row could share a screen and not one
 * rule. That is what makes a page look assembled rather than designed.
 *
 * The rules, and they are few:
 *
 *   Ground is a neutral near-white. Surfaces are white and are told apart
 *   by a hairline, not by a shadow — a shadow on every card gives weight to
 *   everything, which is the same as giving it to nothing.
 *
 *   Colour means state. Green is the brand and marks what is healthy or
 *   primary; amber means attention; red means wrong. Nothing is coloured to
 *   be decorative, because a reader who has learned that amber means "look"
 *   should never be made to check.
 *
 *   One radius. One border. One spacing rhythm.
 *
 *   Figures are set in tabular numerals so a column of money lines up on
 *   the decimal point instead of drifting.
 */
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#07883f', dark: '#04612c', light: '#39a966', contrastText: '#ffffff' },
    secondary: { main: '#0f766e', dark: '#0b544e', light: '#3d9990', contrastText: '#ffffff' },
    success: { main: '#07883f', dark: '#04612c', light: '#39a966' },
    warning: { main: '#b45309' },
    error: { main: '#b3261e' },
    info: { main: '#0f766e' },
    background: { default: '#f7f8f7', paper: '#ffffff' },
    text: { primary: '#14201a', secondary: '#5d6b64', disabled: '#94a29a' },
    divider: '#e3e8e4',
  },
  shape: { borderRadius: 10 },
  typography: {
    fontFamily: 'Inter, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    h4: { fontWeight: 800, letterSpacing: '-0.03em' },
    h5: { fontWeight: 800, letterSpacing: '-0.025em' },
    h6: { fontWeight: 800, letterSpacing: '-0.015em' },
    button: { fontWeight: 800, textTransform: 'none' },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          width: '100%',
          height: '100%',
          margin: 0,
          overflow: 'hidden',
        },
        body: {
          width: '100%',
          height: '100%',
          margin: 0,
          overflow: 'hidden',
          backgroundColor: '#f7f8f7',
          // A column of money reads as a column.
          fontVariantNumeric: 'tabular-nums',
        },
        '#root': {
          width: '100%',
          height: '100%',
          minHeight: '100%',
          overflow: 'hidden',
        },
        '*': {
          boxSizing: 'border-box',
        },
        '*:focus-visible': {
          outline: '3px solid rgba(7,136,63,.28)',
          outlineOffset: 2,
        },
        '*::-webkit-scrollbar': { width: 10, height: 10 },
        '*::-webkit-scrollbar-track': { background: '#f0f2f0' },
        '*::-webkit-scrollbar-thumb': { background: '#c2ccc6', borderRadius: 999, border: '2px solid #f0f2f0' },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 10, minHeight: 40, paddingInline: 16 },
        // A coloured shadow under a green button is decoration; the colour
        // already says it is the primary action.
        contained: { boxShadow: 'none' },
      },
    },
    /*
     * Every surface, the same surface.
     *
     * Panels were arriving with elevation 1, 2 and 3, three radii and a
     * tinted gradient apiece. An outlined paper on a near-white ground
     * separates perfectly well and leaves shadow available for the one
     * thing that genuinely floats: a menu or a dialog.
     */
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          border: '1px solid #e3e8e4',
          borderRadius: 10,
        },
        // Things that really do float keep their lift.
        elevation8: { border: 'none', boxShadow: '0 12px 32px rgba(16,32,26,.14)' },
        elevation16: { border: 'none', boxShadow: '0 18px 44px rgba(16,32,26,.18)' },
        elevation24: { border: 'none', boxShadow: '0 24px 56px rgba(16,32,26,.22)' },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0, variant: 'outlined' },
      styleOverrides: {
        root: { borderColor: '#e3e8e4', borderRadius: 10 },
      },
    },

    MuiDialog: {
      styleOverrides: { paper: { border: 'none' } },
    },

    MuiMenu: {
      styleOverrides: { paper: { border: '1px solid #e3e8e4' } },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: '#fff',
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#77a886' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
        },
      },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: 12, alignItems: 'center' }, message: { fontWeight: 650 } },
    },
    MuiTab: {
      styleOverrides: { root: { textTransform: 'none', fontWeight: 800, minHeight: 44 } },
    },
    MuiTableCell: {
      styleOverrides: {
        root: { borderColor: '#e3e8e4' },
        head: {
          fontWeight: 700,
          fontSize: 11,
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          color: '#5d6b64',
        },
      },
    },

    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 700, borderRadius: 8 },
        sizeSmall: { height: 22, fontSize: 11.5 },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: '#e3e8e4' } },
    },
  },
});

/* ========================= Guards ========================= */

const AppLoading = ({ label = 'Loading workspace…' }) => (
  <Box sx={{ width: '100%', maxWidth: 420, mx: 'auto', p: 3, mt: 6 }} role="status" aria-live="polite">
    <Typography sx={{ mb: 1.25, fontWeight: 800, color: 'text.secondary' }}>{label}</Typography>
    <LinearProgress sx={{ height: 7, borderRadius: 999 }} />
  </Box>
);

const Protected = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <AppLoading label="Checking your secure session…" />;
  }

  return user ? children : <Navigate to="/login" replace />;
};

const DirectorsOnly = ({ children }) => {
  const { user } = useAuth();
  const { platform } = usePlatform();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (String(user.role || '').toLowerCase() !== 'director') {
    return <Navigate to="/login" replace />;
  }

  if (platform?.type !== 'DIRECTORS') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const SiteOnly = ({ children }) => {
  const { user } = useAuth();
  const { platform } = usePlatform();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const role = String(user?.role || '').toLowerCase();

  if (role === 'admin' || role === 'director') {
    return <Navigate to="/login" replace />;
  }

  if (platform?.type !== 'SITE') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

/**
 * Finance is a head-office role: no site, every site.
 * Directors and admin can use the same workspace.
 */
const FinanceOnly = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const role = String(user.role || '').toLowerCase();

  /* Accountants share the finance platform: they need the register, the
   * invoices behind it and the quarterly pack. What they cannot do is change
   * anything, which the server enforces rather than the menu. */
  if (!['finance', 'accountant', 'director', 'admin'].includes(role)) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const AdminOnly = ({ children }) => {
  const { user } = useAuth();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (String(user.role || '').toLowerCase() !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  return children;
};

const GLProtected = ({ children }) => {
  const token = localStorage.getItem('glToken');

  return token ? children : <Navigate to="/gl-manager/login" replace />;
};

/* ========================= Screens ========================= */

function Welcome() {
  const { user } = useAuth();
  const { platform } = usePlatform();

  const name = useMemo(() => {
    const fn = String(user?.firstName || '').trim();
    const ln = String(user?.lastName || '').trim();
    const full = `${fn} ${ln}`.trim();

    return full || user?.email || 'User';
  }, [user]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        p: 2,
        overflow: 'auto',
      }}
    >
      <Paper
        sx={{
          width: '100%',
          maxWidth: 720,
          borderRadius: 3,
          p: { xs: 2.25, sm: 3 },
          boxShadow: 3,
        }}
      >
        <Stack spacing={1.25}>
          <Typography variant="h4" sx={{ fontWeight: 900, lineHeight: 1.1 }}>
            Welcome, {name}
          </Typography>

          {platform?.type ? (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 800 }}
            >
              Current platform: {platform.type}
            </Typography>
          ) : null}

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            sx={{ mt: 1 }}
          >
            <Button
              component={RouterLink}
              to="/login"
              variant="outlined"
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 900,
              }}
            >
              Back to login
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

/* ========================= Routes ========================= */

function AppContent() {
  return (
    <Router>
      <Box
        sx={{
          width: '100%',
          height: '100%',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        <Suspense fallback={<AppLoading />}>
        <Routes>
          {/* PUBLIC */}
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />

          {/* GOLDEN LINES MANAGER */}
          <Route path="/gl-manager/login" element={<GLManagerLogin />} />

          <Route
            path="/gl-manager"
            element={
              <GLProtected>
                <GLManagerShell />
              </GLProtected>
            }
          >
           <Route index element={<Navigate to={DEFAULT_GL_MANAGER_OVERVIEW} replace />} />
<Route path="overview" element={<GLDashboardOverview />} />
<Route path="performance" element={<GLSalesPerformance />} />
<Route path="products" element={<GLManageProducts />} />
<Route path="smart-review" element={<Navigate to={DEFAULT_GL_MANAGER_OVERVIEW} replace />} />
<Route path="sites" element={<GLSites />} />
<Route path="reports" element={<GLReports />} />
<Route
  path="dashboard"
  element={<Navigate to={DEFAULT_GL_MANAGER_OVERVIEW} replace />}
/>
          </Route>

          {/* WELCOME */}
          <Route
            path="/welcome"
            element={
              <Protected>
                <Welcome />
              </Protected>
            }
          />

          {/* ADMIN */}
          <Route
            path="/platform/admin"
            element={
              <Protected>
                <AdminOnly>
                  <Layout />
                </AdminOnly>
              </Protected>
            }
          >
            <Route index element={<AdminHome />} />
            {/* The sidebar used to point here; it is kept so an old link still lands. */}
            <Route path="dashboard" element={<Navigate to="/platform/admin" replace />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="sites" element={<AdminSites />} />
            <Route path="deliveries" element={<AdminDeliveries />} />
            <Route path="settings" element={<AdminSettings />} />
          </Route>

          {/* DIRECTORS */}
          <Route
            path="/platform/directors"
            element={
              <Protected>
                <DirectorsOnly>
                  <Layout />
                </DirectorsOnly>
              </Protected>
            }
          >
            <Route index element={<WeeklyOverview />} />
            <Route path="weekly-overview" element={<WeeklyOverview />} />
            <Route path="expected-tankers" element={<ExpectedTankers />} />
            <Route path="daily-sales" element={<DirectorsDailySales />} />
            <Route
              path="reconciliation-weekly"
              element={<DirectorsWeeklyReconciliation />}
            />
            <Route
              path="reconciliation-daily"
              element={<DirectorsDailyReconciliation />}
            />
            <Route path="director-dashboard" element={<DirectorsDashboard />} />
            <Route path="golden-lines" element={<GoldenLinesDirectorPage />} />
            <Route
              path="drive-offs-nmop"
              element={<DriveOffNmopBoard readOnly />}
            />

            {/* BACS payments */}
            <Route path="payments" element={<DirectorsPaymentsOverview />} />
            <Route path="payments-approval" element={<DirectorsPaymentApproval />} />
            <Route path="payments-build" element={<PaymentsWorkspace />} />
            <Route path="payments-due" element={<PaymentsDuePage />} />
             <Route path="statements" element={<SupplierStatementsPage />} />
            <Route path="spend" element={<SpendAnalyticsPage />} />
            <Route path="suppliers" element={<BeneficiaryDirectory />} />
            <Route path="quarterly-pack" element={<QuarterlyPackPage />} />
          </Route>

          {/* FINANCE */}
          <Route
            path="/platform/finance"
            element={
              <Protected>
                <FinanceOnly>
                  <Layout />
                </FinanceOnly>
              </Protected>
            }
          >
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<DirectorsPaymentsOverview />} />
            <Route path="payments" element={<PaymentsWorkspace />} />
            <Route path="payments-due" element={<PaymentsDuePage />} />
            <Route path="statements" element={<SupplierStatementsPage />} />
            <Route path="spend" element={<SpendAnalyticsPage />} />
            <Route path="suppliers" element={<BeneficiaryDirectory />} />
            <Route path="quarterly-pack" element={<QuarterlyPackPage />} />
          </Route>

          {/* SITE */}
          <Route
            path="/platform/site/:siteCode"
            element={
              <Protected>
                <SiteOnly>
                  <Layout />
                </SiteOnly>
              </Protected>
            }
          >
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<Dashboard />} />
            <Route path="reconciliation" element={<Reconciliation />} />
            <Route path="expected-tankers" element={<ExpectedTankers />} />
            <Route path="daily-sales" element={<DailySales />} />
            <Route path="drive-offs-nmop" element={<DriveOffNmopBoard />} />
            <Route path="golden-lines" element={<GoldenLinesPage />} />
            <Route path="payments" element={<PaymentsWorkspace />} />
            <Route path="payments-due" element={<PaymentsDuePage />} />
             <Route path="statements" element={<SupplierStatementsPage />} />
            <Route path="suppliers" element={<BeneficiaryDirectory />} />
          </Route>

          {/* DEFAULT */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
        </Suspense>
      </Box>
    </Router>
  );
}

export default function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <PlatformProvider>
          <AppContent />
        </PlatformProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
